import {HttpClient} from '@angular/common/http'
import {inject, Injectable} from '@angular/core'
import {UserService} from './rest-api'
import {ContextService} from './context.service'
import {BehaviorSubject, catchError, map, Observable, of, tap} from 'rxjs'

export interface DataTemplate {
  domainData: InheritedDomainData
  userData: UserData
}
export interface InheritedDomainData {
  [n: string]: string
}

export interface UserData {
  [n: string]: string
}

@Injectable({
  providedIn: 'root',
})
export class UserDataService {
  private http = inject(HttpClient)
  private userService = inject(UserService)
  private appContext = inject(ContextService)

  /** * Cache: Composite Key is "domain:userId"
   * Provides synchronous access to preferences once loaded.
   */
  private userDataCache = new Map<string, BehaviorSubject<any>>()

  private getCachedUserDataSubject(
    domain: string,
    userID: string | number,
  ): BehaviorSubject<any> {
    const key = `${domain}:${userID}`
    if (!this.userDataCache.has(key)) {
      this.userDataCache.set(key, new BehaviorSubject<any>(undefined))
    }
    return this.userDataCache.get(key)!
  }

  getUserDataBasedOnContext(): Observable<any> {
    const domain = this.appContext.loginDomain
    const id = this.appContext.userId
    if (!domain || !id) return of(undefined)
    return this.getUserData(domain, id + '') // force string
  }

  /**
   * Retrieves full DataTemplate from cache or API.
   */
  getUserData(domain: string, userID: string | number): Observable<any> {
    const cachedData$ = this.getCachedUserDataSubject(domain, userID)

    // If we have data, return it immediately
    if (cachedData$.getValue() !== undefined) {
      return cachedData$.asObservable()
    }

    // Return the HTTP observable, but "tap" into it to update the cache
    return this.http
      .get<any>(`api://Domains/${domain}/Users/${userID}/getData`)
      .pipe(
        tap((data) => cachedData$.next(data)),
        catchError(() => {
          const fallback = {} // Your minimal structure
          cachedData$.next(fallback)
          return of(fallback)
        }),
      )
  }

  /**
   * Overwrites the entire UserData object.
   */
  setUserData(
    domain: string,
    userID: string | number,
    newData: UserData,
  ): Observable<any> {
    const cachedData$ = this.getCachedUserDataSubject(domain, userID)

    // Call through to adjusted UserService logic
    return this.userService.setUserData(domain, userID + '', newData).pipe(
      tap(() => {
        const currentTemplate = cachedData$.getValue()
        cachedData$.next({
          domainData: currentTemplate?.domainData || {},
          userData: newData,
        } as DataTemplate)
      }),
    )
  }

  //   setUserPreferencesBasedOnContext(newData: UserData): Observable<any> {
  //     const domain = this.appContext.loginDomain;
  //     const id = this.appContext.userId;
  //     if (!domain || !id) return of(null);
  //     return this.setUserPreferences(domain(), id()+"", newData);
  //   }

  /**
   * Helper to specifically check if Force Push is disabled for the current context. (current user i.e. hide template based on this)
   */
  isForcePushDisabled(): Observable<boolean> {
    return this.getUserDataBasedOnContext().pipe(
      map((data) => {
        // 1. Check if the property bag exists
        const featuresString = data?.userData?.['features']
        if (!featuresString) return false

        try {
          // 2. Parse the stringified object
          const features = JSON.parse(featuresString)
          // 3. Compare specifically against the string 'true'
          return features['disableForcePush'] === 'true'
        } catch (e) {
          // If JSON is invalid, assume feature is NOT disabled
          return false
        }
      }),
    )
  }
}
